
import React, { useEffect, useRef } from 'react';
import { AppSection } from '../types';

interface CTAProps {
  onNavigate: (section: AppSection) => void;
}

const CallToAction: React.FC<CTAProps> = ({ onNavigate }) => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="reveal relative bg-slate-900 rounded-[2rem] p-10 md:p-24 overflow-hidden border border-slate-800">
          <div className="absolute inset-0 grid-bg opacity-[0.05]"></div>
          
          <div className="absolute -right-24 -top-24 w-96 h-96 bg-[#0091da]/10 rounded-full blur-3xl"></div>
          <div className="absolute -left-24 -bottom-24 w-96 h-96 bg-[#facc15]/5 rounded-full blur-3xl"></div>
          
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 tracking-tight">
              Ready to scale your technical delivery?
            </h2>
            <p className="text-lg md:text-xl text-slate-400 mb-12 leading-relaxed font-light">
              Whether you need a full-scale digital transformation or a specific senior expert to bridge a gap, Hunargrid is your Chicago partner for Midwest reliability.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
              <div 
                onClick={() => onNavigate(AppSection.Talent)}
                className="reveal p-8 bg-white/[0.03] rounded-2xl border border-white/10 text-left hover:border-[#facc15] transition-all cursor-pointer group hover:bg-white/[0.05]" 
                style={{ transitionDelay: '200ms' }}
              >
                <h3 className="text-xl font-bold text-white mb-2">Hire an Expert</h3>
                <p className="text-sm text-slate-500 mb-8 leading-relaxed font-light">Augment your team with senior Vetted Talent in 48 hours.</p>
                <div className="text-[#facc15] text-xs font-bold uppercase tracking-widest flex items-center group-hover:translate-x-2 transition-transform">
                  Browse the Grid <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </div>
              </div>

              <div 
                onClick={() => onNavigate(AppSection.Solutions)}
                className="reveal p-8 bg-white/[0.03] rounded-2xl border border-white/10 text-left hover:border-[#0091da] transition-all cursor-pointer group hover:bg-white/[0.05]" 
                style={{ transitionDelay: '400ms' }}
              >
                <h3 className="text-xl font-bold text-white mb-2">Request a Solution</h3>
                <p className="text-sm text-slate-500 mb-8 leading-relaxed font-light">Launch a Managed Project with guaranteed delivery outcomes.</p>
                <div className="text-[#0091da] text-xs font-bold uppercase tracking-widest flex items-center group-hover:translate-x-2 transition-transform">
                  Schedule Discovery <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;
